import type { GateContext, Lesson } from '../types';
import { CAPSTONE_CRITERIA as C } from '../presets';
import { evaluateCapstone, f, finalError, run } from './helpers';
import { diffPaths } from '../../state/runRecord';

const PROVIDED = [
  { id: 'm8-bang', label: 'Bang-bang' },
  { id: 'm8-p', label: 'P' },
  { id: 'm8-pi', label: 'PI' },
];

const lowestIae = (ctx: GateContext) =>
  PROVIDED.map((p) => ({ ...p, iae: run(ctx, p.id).stats.iae })).sort((a, b) => a.iae - b.iae)[0].id;

const largestFinalError = (ctx: GateContext) =>
  PROVIDED.map((p) => ({ ...p, e: Math.abs(finalError(run(ctx, p.id), 4)) })).sort((a, b) => b.e - a.e)[0].id;

export const m8: Lesson = {
  id: 'm8',
  number: 8,
  title: 'Tuning your controller',
  summary: 'In this module, you will put your control engineering skills to the test. Compare classic controllers head-to-head on a flight challenge, tune your own PID flight controller to meet tough performance specs, and verify it under new conditions.',
  objectives: [
    'Compare controllers fairly using saved metrics.',
    'Tune a controller to meet published tracking constraints.',
    'Distinguish model prediction, feedback correction and model limitations.',
  ],
  controls: ['controller-kind', 'Kp', 'Ki', 'Kd', 'filter', 'anti-windup', 'bang', 'feedforward', 'contributions', 'damping-analysis'],
  controllerKinds: ['bang-bang', 'p', 'pi', 'pid'],
  sections: [
    {
      id: 'm8-criteria',
      title: 'The published challenge',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'In commercial aerospace certification (such as FAA or EASA airworthiness standards for autonomous aircraft), flight control software must pass strict, quantitative acceptance tests before being certified for flight. A flight controller cannot merely "fly reasonably well." It needs to satisfy strict safety criteria for tracking speed, damping, disturbance rejection, and actuator wear.',
        },
        {
          type: 'p',
          text: 'This challenge puts your flight controller through a 20-second flight profile with two distinct phases: climbing from 2.0 m to 2.5 m at $t = 1\\text{ s}$, and rejecting an unexpected 1 N downward load that strikes at $t = 8\\text{ s}$.',
        },
        {
          type: 'table',
          caption: 'Flight certification pass criteria (all must be satisfied)',
          headers: ['Criterion', 'Numerical requirement', 'Engineering rationale'],
          rows: [
            ['{{overshoot|Overshoot}} after step', `less than ${C.maxOvershoot * 100}% of the 0.5 m step (peak below 2.55 m)`, 'Prevents collisions with overhead ceilings, structures, or terrain during aggressive climbs.'],
            ['{{settling-time|Settling time}} after step', `within ±${C.tolerance} m of 2.5 m within ${C.settleWithin} s`, 'Ensures fast, decisive tracking without lingering oscillations.'],
            ['Settling time after load', `within ±${C.tolerance} m of 2.5 m within ${C.settleWithin} s of 8 s`, 'Verifies robust integral action that rapidly eliminates payload sag.'],
            ['{{saturation|Actuator saturation}}', `at most ${C.maxSaturation} s total duration`, 'Protects motors and speed controllers from overheating and battery voltage sag.'],
          ],
        },
        {
          type: 'p',
          text: 'Once your tuned flight controller passes all criteria, you will subject it to a **transfer test**: the same controller must handle a surprise $2\\text{ N}$ load applied at $10\\text{ s}$ without any retuning! A well-designed flight controller must be robust enough to handle unexpected conditions.',
        },
      ],
    },
    {
      id: 'm8-compare',
      title: 'Compare the three controllers',
      phase: 'experiment',
      blocks: [
        {
          type: 'p',
          text: 'To understand why modern robotics converged on PID control, aerospace engineers run head-to-head flight benchmarks across identical flight challenges.',
        },
        {
          type: 'h',
          text: 'Part 1: The Architecture Benchmark (Steps 1–3)',
        },
        {
          type: 'p',
          text: '**Purpose**: Evaluate Bang-bang, P, and PI side by side on the same 20 s flight: an initial hover, a 0.5 m step climb at 1 s, and an unexpected 1 N downward payload disturbance at 8 s. After running the three steps, click **Compare** in the simulator to see each controller’s signature response: Bang-bang chattering, P offset sag, and PI overshoot without derivative damping.',
        },
        {
          type: 'h',
          text: 'Part 2: Engineering Design, Transfer & Spring-Free Flight (Steps 4–6)',
        },
        {
          type: 'p',
          text: '**Purpose**: In Step 4, tune your own controller to meet rigorous flight performance criteria. In Step 5, submit your controller to an unmodeled transfer stress test (a 2 N load at 10 s). Finally, in Step 6, test your controller on the untethered vehicle with the guide spring removed ($k = 0$) to verify software-only stabilization.',
        },
        { type: 'lab' },
      ],
    },
    {
      id: 'm8-tune',
      title: 'Tune, transfer, and try the free vehicle',
      phase: 'inspect',
      blocks: [
        {
          type: 'p',
          text: 'Now take on the role of lead flight controls engineer. Follow this systematic workflow to tune, certify, and transfer your custom controller:',
        },
        {
          type: 'list',
          ordered: true,
          items: [
            '**Step 4 (Your tuned controller)**: Select your controller architecture (PID is recommended). Adjust one gain at a time. The automated criteria checker below will update after each run, flagging any violated specs.',
            '**Step 5 (Transfer test)**: Once your controller passes all criteria, load the Transfer test. It automatically locks in your tuned gains and tests them against an unmodeled 2 N load. Verify that your design generalizes safely.',
            '**Step 6 (Free-flying vehicle)**: Finally, test your tuned gains on the untethered vehicle preset ($k = 0$). Observe how removing the physical guide spring alters vehicle dynamics and stability.',
          ],
        },
        {
          type: 'callout',
          tone: 'note',
          title: 'Engineering tuning guide',
          text: 'Start from the preset gains and adjust one term at a time. Increasing $K_d$ quells overshoot; increasing $K_i$ accelerates recovery from the 1 N load (at the expense of slight overshoot); increasing $K_p$ speeds up rise time but risks motor saturation.',
        },
      ],
    },
    {
      id: 'm8-explain',
      title: 'Explain',
      phase: 'explain',
      blocks: [
        {
          type: 'reflect',
          id: 'm8-r1',
          prompt: 'Which parts of your controller rely on the model being right (prediction), which parts fix what the model got wrong (correction), and what did the model leave out entirely (limitations)?',
        },
      ],
    },
  ],
  lab: {
    id: 'm8-lab',
    version: 1,
    instructions: 'Complete the flight tests (20 s each): Steps 1–3 benchmark Bang-bang, P, and PI; Step 4 tunes your controller to spec; Steps 5–6 certify generalization in transfer and spring-free flight.',
    steps: [
      {
        id: 'm8-bang',
        title: 'Step 1: Provided bang-bang (±3 N, h = 0.05 m)',
        presetId: 'capstone',
        setup: (c) => Object.assign(c.controller, { kind: 'bang-bang', bangDelta: 3, hysteresis: 0.05 }),
        instructions:
          'Load the step and run to the end (20 s). Watch the thrusters alternate between discrete force levels, locking the drone into a constant limit-cycle bounce around 2.5 m with coarse response to the 8 s load.',
        permitted: [],
        requires: 'A complete 20 s run.',
      },
      {
        id: 'm8-p',
        title: 'Step 2: Provided P ($K_p = 16$)',
        presetId: 'capstone',
        setup: (c) => Object.assign(c.controller, { kind: 'p', Kp: 16 }),
        instructions:
          'Load the step and run to the end (20 s). Notice the fast initial rise to 2.5 m, but watch the craft suffer a permanent altitude sag below target when the 1 N load hits at 8 s.',
        permitted: [],
        requires: 'A complete 20 s run.',
      },
      {
        id: 'm8-pi',
        title: 'Step 3: Provided PI ($K_p = 12$, $K_i = 6$)',
        presetId: 'capstone',
        setup: (c) => Object.assign(c.controller, { kind: 'pi', Kp: 12, Ki: 6 }),
        instructions:
          'Load the step and run to the end (20 s). Watch the integral action eliminate load sag after 8 s, but observe the significant overshoot and oscillatory ringing during the initial 1 s climb due to lack of derivative damping.',
        permitted: [],
        requires: 'A complete 20 s run.',
      },
      {
        id: 'm8-tuned',
        title: 'Step 4: Your tuned controller',
        presetId: 'capstone',
        instructions:
          'Load the step, select your controller architecture (PID recommended), and adjust gains. Run the simulation to the end (20 s) and inspect the automated criteria checker until all metrics show green checkmarks.',
        permitted: ['controller'],
        check: (r) => {
          const res = evaluateCapstone(r);
          return res.pass ? null : res.reasons.join(' ');
        },
        requires: 'A complete run meeting every published criterion.',
      },
      {
        id: 'm8-transfer',
        title: 'Step 5: Transfer test (disclosed: 2 N load at 10 s)',
        presetId: 'capstone-transfer',
        inheritControllerFrom: 'm8-tuned',
        instructions:
          'Load after passing Step 4 (your tuned controller is copied in automatically). Run to the end (20 s) without retuning to verify that your design remains stable and rejects an unexpected 2 N load at 10 s.',
        permitted: ['controller'],
        check: (r, ev) => {
          const tuned = ev['m8-tuned'];
          if (!tuned) return 'Complete Your tuned controller first.';
          return diffPaths(tuned.config.controller, r.config.controller).length === 0
            ? null
            : 'The transfer test must use exactly your tuned controller. Reload this step to copy it.';
        },
        requires: 'A complete 20 s run with your unchanged tuned controller.',
      },
      {
        id: 'm8-free',
        title: 'Step 6: Spring-free preset',
        presetId: 'capstone-free',
        inheritControllerFrom: 'm8-tuned',
        instructions:
          'Load the step (your tuned controller is copied in) and run to the end (20 s). Watch your controller stabilize the untethered drone with the physical spring removed ($k = 0$), proving active flight control in software.',
        permitted: ['controller'],
        requires: 'A complete 20 s run.',
      },
    ],
  },
  gate: {
    id: 'g8',
    version: 1,
    title: 'Capstone check',
    evidenceRequirement: (ctx) => {
      const r = ctx.runs['m8-tuned'];
      if (!r) return 'Record a tuned run that meets the criteria.';
      const res = evaluateCapstone(r);
      return res.pass ? null : res.reasons.join(' ');
    },
    questions: [
      {
        id: 'g8-q1',
        version: 1,
        kind: 'choice',
        binds: PROVIDED.map((p) => p.id),
        prompt: () => 'Of the three provided controllers, which had the smallest accumulated error magnitude (IAE) in your runs?',
        table: (ctx) => ({
          caption: 'Your provided-controller runs',
          headers: ['Controller', 'IAE (m·s)', 'saturation (s)', 'final 4 s mean error (m)'],
          rows: PROVIDED.map((p) => [p.label, f(run(ctx, p.id).stats.iae, 3), f(run(ctx, p.id).stats.saturationTime), f(finalError(run(ctx, p.id), 4), 3)]),
        }),
        options: PROVIDED.map((p) => ({ id: p.id, text: p.label })),
        correct: lowestIae,
        hint: 'Read the IAE column.',
        explanation: 'IAE rewards getting close quickly and staying close.',
      },
      {
        id: 'g8-q2',
        version: 1,
        kind: 'choice',
        binds: PROVIDED.map((p) => p.id),
        prompt: () => 'Which provided controller left the largest mean error over the final 4 s, after the load?',
        options: PROVIDED.map((p) => ({ id: p.id, text: p.label })),
        correct: largestFinalError,
        hint: 'Use the final-error column in the previous table.',
        explanation:
          'Only controllers with an integral term (PI) cancel a steady load among the provided set. P settles with an offset. Bang-bang keeps oscillating around a level that is not centred on the target.',
        review: 'm8-compare',
      },
      {
        id: 'g8-q3',
        version: 1,
        kind: 'match',
        binds: [],
        prompt: () => 'Classify each statement.',
        items: [
          { id: 'ff', text: '$T_0 = m g + k(z_0 - z_s)$ holds the vehicle before any error appears.' },
          { id: 'fb', text: '$u_I$ grows after 8 s until the 1 N load is cancelled.' },
          { id: 'lim1', text: 'Real rotors take time to spin up. This model has no motor lag.' },
          { id: 'lim2', text: 'The linear equations stop applying when thrust saturates or the vehicle touches the ground.' },
        ],
        choices: [
          { id: 'pred', text: 'model prediction' },
          { id: 'corr', text: 'feedback correction' },
          { id: 'limit', text: 'model limitation' },
        ],
        correct: () => ({ ff: 'pred', fb: 'corr', lim1: 'limit', lim2: 'limit' }),
        hint: 'Prediction comes from the model. Correction comes from measurement. Limitations are what the model leaves out.',
        explanation: 'Good controllers combine a model’s prediction with feedback that fixes what the model misses. Engineers stay aware of what the model ignores.',
      },
      {
        id: 'g8-q4',
        version: 1,
        kind: 'numeric',
        binds: ['m8-tuned'],
        prompt: () => 'Report your tuned run’s step overshoot as a percentage of the 0.5 m step (from the metrics or criteria panel).',
        unit: '%',
        answer: (ctx) => (evaluateCapstone(run(ctx, 'm8-tuned')).overshoot ?? 0) * 100,
        tolerance: 0.5,
        hint: 'Overshoot = (peak − 2.5 m) / 0.5 m × 100.',
        explanation: 'Your overshoot number comes from the peak of this run.',
        review: 'm8-criteria',
      },
      {
        id: 'g8-q5',
        version: 1,
        kind: 'choice',
        binds: ['m8-tuned', 'm8-transfer'],
        prompt: () => 'Did your unchanged tuned controller also meet all the criteria on the transfer scenario (2 N load at 10 s)? Check the criteria panel for the transfer run.',
        options: [
          { id: 'yes', text: 'Yes, it met all criteria.' },
          { id: 'no', text: 'No, it missed at least one.' },
        ],
        correct: (ctx) => (evaluateCapstone(run(ctx, 'm8-transfer')).pass ? 'yes' : 'no'),
        hint: 'Select the transfer run in Saved runs to see its criteria check.',
        explanation: 'Testing on a scenario you did not tune for is how you find out whether a design generalizes.',
      },
    ],
  },
};
